import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  FiBriefcase,
  FiCheckCircle,
  FiEdit,
  FiLayers,
  FiPlus,
  FiSearch,
  FiUsers,
  FiX,
  FiXCircle,
} from "react-icons/fi";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";

const emptyForm = {
  code: "",
  name: "",
  description: "",
  isActive: true,
};

export default function Departments() {
  const { accessToken } = useAuth();

  const [departments, setDepartments] = useState([]);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadDepartments = useCallback(async () => {
    if (!accessToken) return;

    setIsLoading(true);
    setError("");

    try {
      const response = await apiRequest("/departments", {
        accessToken,
      });

      setDepartments(response || []);
    } catch (requestError) {
      setDepartments([]);
      setError(
        requestError.message || "Unable to load departments.",
      );
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadDepartments();
  }, [loadDepartments]);

  const filteredDepartments = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return departments.filter((department) => {
      return (
        !normalizedSearch ||
        department.name.toLowerCase().includes(normalizedSearch) ||
        department.code.toLowerCase().includes(normalizedSearch) ||
        department.description
          ?.toLowerCase()
          .includes(normalizedSearch)
      );
    });
  }, [departments, search]);

  const stats = useMemo(() => {
    return {
      total: departments.length,
      active: departments.filter(
        (department) => department.isActive,
      ).length,
      designations: departments.reduce(
        (sum, department) =>
          sum + (department._count?.designations || 0),
        0,
      ),
      employees: departments.reduce(
        (sum, department) =>
          sum + (department._count?.assignments || 0),
        0,
      ),
    };
  }, [departments]);

  const openCreateModal = () => {
    setEditingDepartment(null);
    setModalOpen(true);
  };

  const openEditModal = (department) => {
    setEditingDepartment(department);
    setModalOpen(true);
  };

  const saveDepartment = async (form) => {
    setSaving(true);
    setError("");

    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        ...(editingDepartment
          ? { isActive: form.isActive }
          : {}),
      };

      await apiRequest(
        editingDepartment
          ? `/departments/${editingDepartment.id}`
          : "/departments",
        {
          method: editingDepartment ? "PATCH" : "POST",
          body: payload,
          accessToken,
        },
      );

      setModalOpen(false);
      setEditingDepartment(null);
      await loadDepartments();
    } catch (requestError) {
      setError(
        requestError.message || "Unable to save department.",
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleDepartmentStatus = async (department) => {
    const action = department.isActive ? "deactivate" : "activate";

    if (
      !window.confirm(
        `Do you want to ${action} ${department.name}?`,
      )
    ) {
      return;
    }

    setError("");

    try {
      if (department.isActive) {
        await apiRequest(`/departments/${department.id}`, {
          method: "DELETE",
          accessToken,
        });
      } else {
        await apiRequest(`/departments/${department.id}`, {
          method: "PATCH",
          accessToken,
          body: { isActive: true },
        });
      }

      await loadDepartments();
    } catch (requestError) {
      setError(
        requestError.message ||
          `Unable to ${action} department.`,
      );
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Departments
          </h1>

          <p className="mt-2 text-gray-500">
            Manage departments and employee allocations.
          </p>
        </div>

        <button
          onClick={openCreateModal}
          className="px-5 py-3 rounded-2xl bg-primary-800 text-white flex items-center gap-2 hover:bg-primary-900 transition"
        >
          <FiPlus />
          Add Department
        </button>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        <StatCard
          title="Total Departments"
          value={stats.total}
          icon={<FiLayers size={22} />}
          iconBg="bg-primary-50"
          iconColor="text-primary-700"
        />

        <StatCard
          title="Active Departments"
          value={stats.active}
          icon={<FiCheckCircle size={22} />}
          iconBg="bg-green-50"
          iconColor="text-green-600"
        />

        <StatCard
          title="Designations"
          value={stats.designations}
          icon={<FiBriefcase size={22} />}
          iconBg="bg-blue-50"
          iconColor="text-blue-600"
        />

        <StatCard
          title="Employees Assigned"
          value={stats.employees}
          icon={<FiUsers size={22} />}
          iconBg="bg-yellow-50"
          iconColor="text-yellow-600"
        />
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 p-6 shadow-sm">
        <div className="relative max-w-md">
          <FiSearch
            className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
            size={18}
          />

          <input
            type="text"
            placeholder="Search department..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="w-full pl-12 pr-4 py-3 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">
            Department List
          </h3>
        </div>

        {isLoading ? (
          <div className="min-h-72 flex items-center justify-center">
            <div className="h-10 w-10 rounded-full border-4 border-primary-100 border-t-primary-800 animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                    Department
                  </th>

                  <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                    Designations
                  </th>

                  <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                    Employees
                  </th>

                  <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                    Status
                  </th>

                  <th className="px-6 py-4 text-center text-xs uppercase text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {filteredDepartments.map((department) => (
                  <tr
                    key={department.id}
                    className="border-t border-gray-100 hover:bg-gray-50"
                  >
                    <td className="px-6 py-5">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-gray-900">
                            {department.name}
                          </p>

                          <span className="rounded-md bg-primary-50 px-2 py-1 text-xs font-semibold text-primary-800">
                            {department.code}
                          </span>
                        </div>

                        <p className="mt-1 text-sm text-gray-500">
                          {department.description ||
                            "No description added"}
                        </p>
                      </div>
                    </td>

                    <td className="px-6 py-5 text-gray-600">
                      {department._count?.designations || 0}
                    </td>

                    <td className="px-6 py-5 font-semibold text-primary-700">
                      {department._count?.assignments || 0}
                    </td>

                    <td className="px-6 py-5">
                      <StatusBadge isActive={department.isActive} />
                    </td>

                    <td className="px-6 py-5">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => openEditModal(department)}
                          className="h-10 w-10 rounded-xl bg-blue-50 text-blue-600 hover:bg-blue-100 flex items-center justify-center transition"
                          title="Edit department"
                        >
                          <FiEdit />
                        </button>

                        <button
                          onClick={() =>
                            toggleDepartmentStatus(department)
                          }
                          className={`px-3 h-10 rounded-xl text-xs font-semibold transition ${
                            department.isActive
                              ? "bg-red-50 text-red-700 hover:bg-red-100"
                              : "bg-green-50 text-green-700 hover:bg-green-100"
                          }`}
                        >
                          {department.isActive
                            ? "Deactivate"
                            : "Activate"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {!filteredDepartments.length ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-6 py-16 text-center text-gray-500"
                    >
                      No departments found.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <DepartmentModal
        open={modalOpen}
        initialData={editingDepartment}
        saving={saving}
        onClose={() => {
          if (!saving) {
            setModalOpen(false);
            setEditingDepartment(null);
          }
        }}
        onSave={saveDepartment}
      />
    </div>
  );
}

function DepartmentModal({
  open,
  initialData,
  saving,
  onClose,
  onSave,
}) {
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!open) return;

    setForm(
      initialData
        ? {
            code: initialData.code || "",
            name: initialData.name || "",
            description: initialData.description || "",
            isActive: Boolean(initialData.isActive),
          }
        : emptyForm,
    );
  }, [open, initialData]);

  if (!open) return null;

  const updateField = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden">
        <div className="flex justify-between items-center px-8 py-6 border-b border-gray-100">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">
              {initialData ? "Edit Department" : "Add Department"}
            </h3>

            <p className="mt-1 text-sm text-gray-500">
              Configure department details for HRMS.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="h-10 w-10 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center disabled:opacity-50"
          >
            <FiX size={20} />
          </button>
        </div>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            onSave(form);
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 p-8">
            <div>
              <label className="block text-sm text-gray-600 mb-2">
                Department Name *
              </label>

              <input
                required
                maxLength={150}
                value={form.name}
                onChange={(event) =>
                  updateField("name", event.target.value)
                }
                placeholder="Operations"
                className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-2">
                Department Code *
              </label>

              <input
                required
                maxLength={50}
                value={form.code}
                onChange={(event) =>
                  updateField(
                    "code",
                    event.target.value.toUpperCase(),
                  )
                }
                placeholder="OPS"
                className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm text-gray-600 mb-2">
                Description
              </label>

              <textarea
                rows="4"
                value={form.description}
                onChange={(event) =>
                  updateField("description", event.target.value)
                }
                placeholder="Describe the department responsibilities..."
                className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-200 resize-none"
              />
            </div>

            {initialData ? (
              <div className="md:col-span-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(event) =>
                      updateField("isActive", event.target.checked)
                    }
                    className="h-5 w-5 accent-primary-800"
                  />

                  <span className="text-sm font-medium text-gray-700">
                    Department is active
                  </span>
                </label>
              </div>
            ) : null}
          </div>

          <div className="flex justify-end gap-3 px-8 py-5 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-5 py-3 rounded-2xl border border-gray-200 disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving}
              className="px-5 py-3 rounded-2xl bg-primary-800 text-white disabled:opacity-50"
            >
              {saving
                ? "Saving..."
                : initialData
                  ? "Save Changes"
                  : "Save Department"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function StatCard({
  title,
  value,
  icon,
  iconBg,
  iconColor,
}) {
  return (
    <div className="bg-white rounded-3xl border border-gray-100 p-6 shadow-sm">
      <div className="flex justify-between items-center">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <h3 className="mt-3 text-3xl font-bold text-gray-900">
            {value}
          </h3>
        </div>

        <div
          className={`h-14 w-14 rounded-2xl flex items-center justify-center ${iconBg} ${iconColor}`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ isActive }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${
        isActive
          ? "bg-green-50 text-green-700 border-green-100"
          : "bg-red-50 text-red-700 border-red-100"
      }`}
    >
      {isActive ? (
        <FiCheckCircle size={13} />
      ) : (
        <FiXCircle size={13} />
      )}

      {isActive ? "Active" : "Inactive"}
    </span>
  );
}